import { subscribeAppResume } from "../services/appResumeService";
import { Capacitor } from "@capacitor/core";
import { androidDosyaPaylas } from "../utils/androidFileShare";
import { useEffect, useState, useRef } from "react";
import { LANGUAGES, useLanguage } from "../i18n/LanguageContext";
import { rt } from "../i18n/runtime";
import {
  apiRequest,
  saveSession,
  clearSession,
  clearUserScopedStorage,
  getStoredUser,
  getAccessToken,
  getRefreshToken,
  isNetworkError,
} from "../services/apiClient";
import {
  getCurrentSocialIdToken,
  revokeAppleCredential,
  signOutSocialAuthentication,
  startSocialAuthentication,
} from "../services/socialAuthService";
import { areAndroidHealthFeaturesDisabled, isAndroidPlatform } from "../utils/platformFeatures";
import { voipKaydiniKaldir } from "../services/nativeCallBridge";
import { clearNativeBackgroundLocationSession } from "../services/nativeLocationService";
import {
  profilFotografiSilService,
  profilFotografiYukleService,
} from "../services/profilePictureService";
import {
  getPendingInstallAttribution,
  markInstallAttributionConsumed,
  reportPendingInstallAttribution,
} from "../services/installAttributionService";
import { logAnalyticsEvent } from "../services/analyticsService";
import { registrationFieldError } from "../utils/registrationValidation";
import { getAppContext } from "../services/feedbackService";

export const mergeAuthProfilePreservingFamilyContext = (current, incoming) => {
  if (!current) return incoming || null;
  if (!incoming) return current;
  const activeFamilyId = current.familyId || current.aktivFamilyId;
  if (!activeFamilyId) return { ...current, ...incoming };
  return {
    ...current,
    ...incoming,
    familyId: activeFamilyId,
    aktivFamilyId: current.aktivFamilyId || activeFamilyId,
    aileYetkisi: current.aileYetkisi,
    permissions: current.permissions,
  };
};

export const isSameVerificationEmail = (nextEmail, currentEmail) => {
  const normalizedNextEmail = String(nextEmail || "").trim().toLowerCase();
  const normalizedCurrentEmail = String(currentEmail || "").trim().toLowerCase();
  return !!normalizedNextEmail && !!normalizedCurrentEmail && normalizedNextEmail === normalizedCurrentEmail;
};

export const buildAccountDeletionRequestBody = ({ body, deletionSurvey, appContext = {}, language = "tr" }) => {
  const nextBody = { ...(body || {}) };
  if (!deletionSurvey) return nextBody;
  nextBody.deletionSurvey = {
    ...deletionSurvey,
    platform: appContext.platform || "web",
    appVersion: appContext.appVersion || "",
    buildNumber: appContext.buildNumber || "",
    osVersion: appContext.osVersion || "",
    deviceModel: appContext.deviceModel || "",
    language: language === "en" ? "en" : "tr",
    schemaVersion: 1,
  };
  return nextBody;
};

export const resetAuthRegistrationState = ({
  setEmail,
  setKayitSifre,
  setAd,
  setSoyad,
  setDogumTarihi,
  setCinsiyet,
  setTelefon,
  setKullaniciAdi,
  setRol,
  setKanGrubu,
  setBoy,
  setKilo,
  setKayitModu,
  setYasalOnay,
  setKayitAlanHatasi,
  setBekleyenSosyalKayit,
}) => {
  setEmail("");
  setKayitSifre("");
  setAd("");
  setSoyad("");
  setDogumTarihi("");
  setCinsiyet("Belirtmek istemiyorum");
  setTelefon("");
  setKullaniciAdi("");
  setRol("Kişisel");
  setKanGrubu("");
  setBoy("");
  setKilo("");
  setKayitModu(false);
  setYasalOnay(false);
  setKayitAlanHatasi("");
  setBekleyenSosyalKayit(null);
};

export function useAuth() {
  // Ekranda gösterilen dille birebir aynı kaynak — localStorage'ı ayrıca
  // okumak (ki bazı durumlarda Context state ile senkron olmayabiliyordu)
  // yerine doğrudan Context'ten alıyoruz.
  const { lang: aktifDil } = useLanguage();
  // Cache'den anında başlat — API gelmeden önce kullanıcı bilgileri görünsün
  const _su = (() => { try { const r = localStorage.getItem("fp:user"); return r ? JSON.parse(r) : null; } catch { return null; } })();
  const _fc = (() => { try { const r = localStorage.getItem("fp:familyCache"); return r ? JSON.parse(r) : null; } catch { return null; } })();
  const _hasCacheInit = !!(_su && _fc);

  const [user, setUser]                               = useState(() => _su ? {
    ..._su,
    uid: _su.uid || _su.firebase_uid || _su.id,
    firebase_uid: _su.firebase_uid || _su.uid || _su.id,
    emailVerified: !!_su.emailVerified,
    getIdToken: async () => getAccessToken(),
    reload: async () => {},
  } : null);
  const [profil, setProfil]                           = useState(() => _su?.profil || _su || null);
  const [authKontrolEdiliyor, setAuthKontrolEdiliyor] = useState(!_hasCacheInit);
  const [emailDogrulandi, setEmailDogrulandi]         = useState(_hasCacheInit ? (!!_su?.emailVerified) : false);
  const [dogrulamaMailiGonderildi, setDogrulamaMailiGonderildi] = useState(null);

  /* Giriş formu */
  const [loginBilgi, setLoginBilgi]   = useState("");
  const [sifreGoster, setSifreGoster] = useState(false);
  const [email, setEmail]             = useState("");
  const [sifre, setSifre]             = useState("");
  const [kayitSifre, setKayitSifre]   = useState("");
  const sifreInputRef                 = useRef(null);
  const [hata, setHata]               = useState("");
  const [bilgiMesaji, setBilgiMesaji] = useState("");
  const [yukleniyor, setYukleniyor]   = useState(false);
  const [kayitModu, setKayitModu]     = useState(false);
  const [yasalOnay, setYasalOnay]       = useState(false);
  const [bekleyenSosyalKayit, setBekleyenSosyalKayit] = useState(null);
  const [kayitAlanHatasi, setKayitAlanHatasi] = useState("");

  /* Kayıt formu */
  const [ad, setAd]                     = useState("");
  const [soyad, setSoyad]               = useState("");
  const [dogumTarihi, setDogumTarihi]   = useState("");
  const [cinsiyet, setCinsiyet]         = useState("Belirtmek istemiyorum");
  const [telefon, setTelefon]           = useState("");
  const [kullaniciAdi, setKullaniciAdi] = useState("");
  const [rol, setRol]                   = useState("Kişisel");
  const [kanGrubu, setKanGrubu]         = useState("");
  const [boy, setBoy]                   = useState("");
  const [kilo, setKilo]                 = useState("");

  const profilDinleyiciRef = useRef(null);

  useEffect(() => {
    reportPendingInstallAttribution().then((attribution) => {
      // Zaten hesabı bulunan bir yeniden kurulum, yeni üyelik dönüşümü değildir.
      if (attribution && getStoredUser()) markInstallAttributionConsumed();
    });
  }, []);

  const profilDinlemeyiDurdur = () => {
    if (typeof profilDinleyiciRef.current === "function") {
      profilDinleyiciRef.current();
      profilDinleyiciRef.current = null;
    }
  };

  // Backend user objesini uygulamanın beklediği formata çevir
  const normalizeUser = (backendUser) => {
    if (!backendUser) return null;
    const uid = backendUser.uid || backendUser.firebase_uid || backendUser.id;
    return {
      ...backendUser,
      uid,
      firebase_uid: backendUser.firebase_uid || uid,
      // Firebase Auth uyumlu alanlar
      emailVerified: !!backendUser.emailVerified,
      getIdToken: async () => getAccessToken(),
      reload: async () => {},
    };
  };

  const sosyalHataMesaji = (error) => {
    const code = String(error?.code || "");
    if (code === "ACCOUNT_EXISTS_DIFFERENT_METHOD") return rt("auth.social.error.existingAccount");
    if (code === "SOCIAL_IDENTITY_IN_USE") return rt("auth.social.error.identityInUse");
    if (code === "SOCIAL_AUTH_CANCELLED") return "";
    if (code === "SOCIAL_GOOGLE_NO_CREDENTIAL") return rt("auth.social.error.googleNoCredential");
    if (code === "SOCIAL_GOOGLE_PROVIDER_UNAVAILABLE") return rt("auth.social.error.googleProviderUnavailable");
    if (code === "SOCIAL_GOOGLE_PLAY_SERVICES") return rt("auth.social.error.googlePlayServices");
    if (code === "SOCIAL_GOOGLE_CONFIGURATION") return rt("auth.social.error.googleConfiguration");
    if (code === "SOCIAL_NETWORK_ERROR") return rt("auth.social.error.network");
    if (code === "SOCIAL_AUTH_BUSY") return rt("auth.social.error.busy");
    if (code === "SOCIAL_AUTH_TIMEOUT") return rt("auth.social.error.timeout");
    if (code === "SOCIAL_TOKEN_MISSING" || code === "SOCIAL_TOKEN_INVALID") return rt("auth.social.error.verifyFailed");
    if (code === "APPLE_REVOCATION_TOKEN_MISSING") return rt("auth.social.error.appleDeleteRetry");
    if (code.startsWith("auth/")) return rt("auth.social.error.generic");
    // Native SDK'nin teknik İngilizce hata metni kullanıcıya asla gösterilmez.
    return rt("auth.social.error.generic");
  };

  const oturumuUygula = (data) => {
    saveSession(data);
    const normalizedUser = normalizeUser(data?.user);
    const nextProfile = data?.user?.profil || data?.user || null;
    setUser(normalizedUser);
    setProfil(nextProfile);
    setEmailDogrulandi(!!data?.user?.emailVerified);
    setDogrulamaMailiGonderildi(null);
    const uid = data?.user?.uid || data?.user?.firebase_uid || data?.user?.id;
    if (uid) profilDinle(uid, null);
    return normalizedUser;
  };

  // Profili backend'den al (Firestore kaldırıldı)
  const profilDinle = (uid, onLangSync) => {
    profilDinlemeyiDurdur();
    // Backend'den profil polling — her 5 dakikada bir güncelle
    const yukle = async () => {
      try {
        const data = await apiRequest("/auth/me");
        if (data?.user) {
          const incomingProfile = data.user?.profil || data.user;
          // /auth/me kimlik/profil yenilemesidir. Aktif aile rolü ve izinleri
          // family_members kaynağından useFamily tarafından yönetilir; auth
          // snapshot'ındaki eski genel alanlar bunları geri açmamalıdır.
          setProfil((current) => mergeAuthProfilePreservingFamilyContext(current, incomingProfile));
          const lang = data.user?.language || data.user?.profil?.language;
          if (lang && LANGUAGES.includes(lang)) {
            try { localStorage.setItem("appLanguage", lang); } catch {}
            onLangSync?.(lang);
          }
        }
      } catch (_) {}
    };
    yukle();
    const id = setInterval(yukle, 5 * 60 * 1000);

    // Uygulama ön plana geldiğinde (iOS home'dan dön, sekme değiştir vb.)
    // profil hemen yenilenir → yetki değişiklikleri anında yansır
    const unsubscribeResume = subscribeAppResume(yukle, { delayMs: 1050 });

    profilDinleyiciRef.current = () => {
      clearInterval(id);
      unsubscribeResume();
    };
  };

  /* ── Auth takibi — uygulama açılışında localStorage'dan oturum yükle ── */
  const startAuthWatch = (onLogin, onLogout, onLangSync) => {
    setAuthKontrolEdiliyor(true);

    // Session expired eventi dinle
    const onExpired = () => {
      clearNativeBackgroundLocationSession().catch(() => {});
      profilDinlemeyiDurdur();
      setUser(null); setProfil(null); setEmailDogrulandi(false);
      onLogout?.();
    };
    window.addEventListener("fp:sessionExpired", onExpired);

    const storedUser = getStoredUser();
    const token      = getAccessToken();
    const refreshSession = getRefreshToken();

    if (!storedUser || (!token && !refreshSession)) {
      clearNativeBackgroundLocationSession().catch(() => {});
      setAuthKontrolEdiliyor(false);
      onLogout?.();
      return () => window.removeEventListener("fp:sessionExpired", onExpired);
    }

    // Yerel oturum mevcut — kullanıcıyı hemen yükle (offline durumda da çalışsın)
    const localUser = normalizeUser(storedUser);
    setUser(localUser);
    setEmailDogrulandi(!!storedUser.emailVerified);
    // Profili de localStorage'dan hemen set et — avatar ve ad/soyad offline'da da görünsün
    const localProfil = storedUser?.profil || storedUser;
    if (localProfil) setProfil(localProfil);
    setAuthKontrolEdiliyor(false);
    // onLogin'i yerel user ile çağır; arka planda /auth/me doğrulaması gelince
    // gerekirse güncelleme yapar
    onLogin?.(localUser, localProfil, false);

    // Arka planda /auth/me ile token doğrula ve güncel veriyi al
    (async () => {
      try {
        const data = await apiRequest("/auth/me");
        const normalizedUser = normalizeUser(data.user);
        saveSession({ user: data.user });
        setUser(normalizedUser);
        setEmailDogrulandi(!!data.user?.emailVerified);

        const lang = data.user?.language || data.user?.profil?.language;
        if (lang && LANGUAGES.includes(lang)) {
          try { localStorage.setItem("appLanguage", lang); } catch {}
          onLangSync?.(lang);
        }

        // Profili backend'den dinle
        const uid = data.user?.uid || data.user?.firebase_uid || data.user?.id;
        if (uid) profilDinle(uid, onLangSync);

        // Arka plan güncelleme — onLogin'i tekrar çağır (veri tazeleme)
        await onLogin?.(normalizedUser, data.user?.profil || data.user, true);
      } catch (err) {
        const status = Number(err?.status || 0);
        const temporaryServiceError =
          isNetworkError(err) ||
          err?.code === "service_unavailable" ||
          status === 429 ||
          status === 500 ||
          status === 502 ||
          status === 503 ||
          status === 504;

        if (temporaryServiceError) {
          // Ağ, backend veya veritabanı geçici olarak kullanılamıyor.
          // Yerel oturumu/tokenları koru; uygulama cache ile açılmaya devam etsin.
          // Bağlantı geri geldiğinde profil polling'i güncel veriyi kendisi alır.
          const uid = storedUser?.uid || storedUser?.firebase_uid || storedUser?.id;
          if (uid) profilDinle(uid, onLangSync);
        } else {
          // Yalnızca doğrulanmış kalıcı auth hatalarında oturumu temizle.
          clearNativeBackgroundLocationSession().catch(() => {});
          clearSession();
          setUser(null); setProfil(null); setEmailDogrulandi(false);
          onLogout?.();
        }
      }
    })();

    return () => {
      window.removeEventListener("fp:sessionExpired", onExpired);
      profilDinlemeyiDurdur();
    };
  };

  /* ── Giriş yap ── */
  const girisYap = async () => {
    setHata(""); setBilgiMesaji("");
    if (!loginBilgi || !sifre) { setHata(rt("auth.error.loginRequired")); return; }
    try {
      setYukleniyor(true); setAuthKontrolEdiliyor(true);
      const data = await apiRequest("/auth/login", {
        method: "POST",
        body: JSON.stringify({ login: loginBilgi.trim(), password: sifre }),
      });
      saveSession(data);
      const normalizedUser = normalizeUser(data.user);
      const nextProfile = data.user?.profil || data.user || null;
      setUser(normalizedUser);
      setProfil(nextProfile);
      setEmailDogrulandi(!!data.user?.emailVerified);
      setDogrulamaMailiGonderildi(null);

      const uid2 = data.user?.uid || data.user?.firebase_uid || data.user?.id;
      if (uid2) profilDinle(uid2, null);
      logAnalyticsEvent("login", { method: "password" });
      return normalizedUser;
    } catch (e) {
      setAuthKontrolEdiliyor(false);
      setHata(
        e?.code === "invalid_credentials" || e?.status === 401
          ? rt("auth.error.invalidCredentials")
          : rt("auth.error.loginFailed")
      );
      return null;
    } finally {
      setYukleniyor(false);
      setAuthKontrolEdiliyor(false);
    }
  };

  /* ── Google / Apple ile giriş ── */
  const sosyalGirisYap = async (provider) => {
    setHata(""); setBilgiMesaji("");
    setKayitAlanHatasi("");
    try {
      setYukleniyor(true);
      setAuthKontrolEdiliyor(true);
      // Apple girişi Android'de harici bir güvenli tarayıcı sekmesinde (Chrome
      // Custom Tab) yürür: kullanıcı Apple ID + şifre + 2FA girer, ardından
      // uygulamaya döner. Bu akış kolayca 18 saniyeyi aşabildiğinden, sabit
      // timeout callback'i keserek "sosyal giriş tamamlanamadı" hatasına yol
      // açıyordu. Bu nedenle Apple'da (native) timeout UYGULANMAZ; kullanıcının
      // Apple ekranında geçirdiği süre sınırlanamaz. Google native SDK ise anlık
      // sonuç döndürdüğü için onda mevcut koruyucu timeout korunur.
      const isNative = Capacitor.isNativePlatform();
      const useTimeout = isNative && provider !== "apple.com";
      const socialRequest = startSocialAuthentication(provider, aktifDil || "tr");
      const social = useTimeout
        ? await Promise.race([
            socialRequest,
            new Promise((_, reject) => {
              // OEM cihazlarda Play Services / hesap seçici ilk açılışta birkaç
              // saniye gecikebilir. 18 sn, çalışan akışı erken kesiyordu.
              // 60 sn yalnızca koruyucu üst sınırdır; kullanıcı iptalinde native
              // SDK zaten anında döner.
              window.setTimeout(() => {
                const timeoutError = new Error("SOCIAL_AUTH_TIMEOUT");
                timeoutError.code = "SOCIAL_AUTH_TIMEOUT";
                reject(timeoutError);
              }, 60000);
            }),
          ])
        : await socialRequest;
      const data = await apiRequest("/auth/social", {
        method: "POST",
        body: JSON.stringify({ idToken: social.idToken }),
      });

      if (data?.onboardingRequired) {
        const socialProfile = data.socialProfile || {};
        setBekleyenSosyalKayit({ provider: social.provider });
        setEmail(String(socialProfile.email || "").trim().toLowerCase());
        setAd(String(socialProfile.ad || "").trim());
        setSoyad(String(socialProfile.soyad || "").trim());
        setKayitSifre("");
        setKayitModu(true);
        return { onboardingRequired: true };
      }

      const normalizedUser = oturumuUygula(data);
      setBekleyenSosyalKayit(null);
      logAnalyticsEvent("login", { method: provider === "apple.com" ? "apple" : "google" });
      return normalizedUser;
    } catch (error) {
      setAuthKontrolEdiliyor(false);
      const message = sosyalHataMesaji(error);
      if (message) setHata(message);
      if (error?.code !== "SOCIAL_AUTH_CANCELLED") {
        await signOutSocialAuthentication();
      }
      return null;
    } finally {
      setYukleniyor(false);
      setAuthKontrolEdiliyor(false);
    }
  };

  const sosyalAkisiIptalEt = async () => {
    setBekleyenSosyalKayit(null);
    setKayitModu(false);
    setHata("");
    setBilgiMesaji("");
    setYasalOnay(false);
    setKayitAlanHatasi("");
    await signOutSocialAuthentication();
  };

  /* ── Kayıt ol ── */
  const kayitOl = async () => {
    setHata(""); setBilgiMesaji("");
    setKayitAlanHatasi("");
    const sosyalKayit = !!bekleyenSosyalKayit;
    const fieldError = registrationFieldError({
      name: ad,
      surname: soyad,
      birthDate: dogumTarihi,
      username: kullaniciAdi,
      email,
      password: kayitSifre,
      socialRegistration: sosyalKayit,
    });
    if (fieldError) {
      setKayitAlanHatasi(fieldError);
      return;
    }
    if (!yasalOnay) {
      setHata(rt("auth.error.legalConsentRequired")); return;
    }
    const telefonRakamlar = String(telefon || "").replace(/\D/g, "");
    const normalizeTelefon = telefonRakamlar ? `+${telefonRakamlar}` : "";
    try {
      setYukleniyor(true);
      // Önceki hesaba ait aile/bildirim/offline cache kayıt sırasında taşınmamalı.
      clearUserScopedStorage();
      const idToken = sosyalKayit ? await getCurrentSocialIdToken() : "";
      const acquisition = await getPendingInstallAttribution();
      const data = await apiRequest(sosyalKayit ? "/auth/social/register" : "/auth/register", {
        method: "POST",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          ...(!sosyalKayit ? { password: kayitSifre } : { idToken }),
          username: kullaniciAdi.trim().toLowerCase(),
          ...(normalizeTelefon ? { phone: normalizeTelefon } : {}),
          ad: ad.trim(), soyad: soyad.trim(),
          dogumTarihi, cinsiyet, rol,
          ...(!areAndroidHealthFeaturesDisabled() ? { kanGrubu, boy, kilo } : {}),
          language: aktifDil || "tr",
          registrationPolicyVersion: 2,
          legalAccepted: true,
          legalVersion: "1.7.1 (22)",
          ...(acquisition ? { acquisition } : {}),
        }),
      });
      if (acquisition) {
        await markInstallAttributionConsumed();
      }
      saveSession(data);
      const normalizedUser = normalizeUser(data.user);
      setUser(normalizedUser);
      setProfil(data.user?.profil || data.user || null);
      setEmailDogrulandi(!!data.user?.emailVerified);
      const verificationSent = sosyalKayit || data?.verificationEmail?.sent !== false;
      setDogrulamaMailiGonderildi(sosyalKayit ? null : verificationSent);
      if (!sosyalKayit && !verificationSent) {
        setBilgiMesaji("");
        setHata(rt("auth.error.verificationInitialSendFailed"));
      } else {
        setBilgiMesaji(rt("auth.info.registerSuccess") || "Kayıt başarılı!");
      }
      setYasalOnay(false);
      setKayitSifre("");
      setBekleyenSosyalKayit(null);
      logAnalyticsEvent("sign_up", { method: sosyalKayit ? (bekleyenSosyalKayit?.provider === "apple.com" ? "apple" : "google") : "password" });
      return data.user;
    } catch (e) {
      const code = String(e?.code || "").toUpperCase();
      const validationCode = {
        NAME_TOO_SHORT: "name_min",
        SURNAME_TOO_SHORT: "surname_min",
        EMAIL_INVALID: "email_invalid",
      }[code];
      const field = String(e?.payload?.field || (
        code === "USERNAME_TAKEN" ? "username"
          : code === "EMAIL_TAKEN" ? "email"
            : code === "PHONE_TAKEN" ? "phone"
              : ""
      ));
      setKayitAlanHatasi(validationCode || field);
      if (validationCode) setHata("");
      else if (field === "username") setHata(rt("auth.error.usernameTaken"));
      else if (field === "email") setHata(rt("auth.error.emailTaken"));
      else if (field === "phone") setHata(rt("auth.error.phoneTaken"));
      else setHata(sosyalKayit ? sosyalHataMesaji(e) : e.message);
    } finally {
      setYukleniyor(false);
    }
  };

  /* ── Çıkış ── */
  const cikisYap = async () => {
    setSifre(""); setLoginBilgi(""); setHata(""); setBilgiMesaji("");
    profilDinlemeyiDurdur();
    // Auth token hâlâ geçerliyken logout/PushKit kaldırma tamamlanabilir; buna
    // karşılık aileye bağlı SSE, presence ve konum tüketicileri hemen kapanır.
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("fp:sessionEnding"));
    }
    await clearNativeBackgroundLocationSession().catch(() => {});
    const refreshToken = getRefreshToken();
    try {
      // Yetkili oturum hâlâ varken cihazın PushKit kaydını kaldır.
      await voipKaydiniKaldir();
      await apiRequest("/auth/logout", {
        method: "POST",
        body: JSON.stringify({ refreshToken }),
      });
    } catch {}
    clearSession({ clearUserData: true });
    setUser(null);
    setProfil(null);
    setEmailDogrulandi(false);
    setDogrulamaMailiGonderildi(null);
    resetAuthRegistrationState({
      setEmail,
      setKayitSifre,
      setAd,
      setSoyad,
      setDogumTarihi,
      setCinsiyet,
      setTelefon,
      setKullaniciAdi,
      setRol,
      setKanGrubu,
      setBoy,
      setKilo,
      setKayitModu,
      setYasalOnay,
      setKayitAlanHatasi,
      setBekleyenSosyalKayit,
    });
    await signOutSocialAuthentication();
  };

  const sosyalHesapBagla = async (provider) => {
    setHata(""); setBilgiMesaji("");
    try {
      setYukleniyor(true);
      const social = await startSocialAuthentication(provider, aktifDil || "tr");
      const data = await apiRequest("/auth/social/link", {
        method: "POST",
        body: JSON.stringify({ idToken: social.idToken }),
      });
      saveSession({ user: data.user });
      const normalizedUser = normalizeUser(data.user);
      setUser(normalizedUser);
      setProfil(data.user?.profil || data.user || null);
      setBilgiMesaji(rt("auth.social.linkSuccess"));
      return data.user;
    } catch (error) {
      await signOutSocialAuthentication();
      const message = sosyalHataMesaji(error);
      const normalized = new Error(message || rt("auth.social.error.generic"));
      normalized.code = error?.code || "SOCIAL_LINK_FAILED";
      throw normalized;
    } finally {
      setYukleniyor(false);
    }
  };

  /* ── Profil güncelle ── */
  // onUyeGuncelle: avatar/renk değişince useFamily'deki uyeler listesini de günceller
  const profilGuncelle = async ({ yeniAd, yeniSoyad, yeniTelefon, yeniDogumTarihi, yeniAvatar, yeniCalendarColor }, onUyeGuncelle) => {
    if (!user) return;
    const guncel = {};
    if (yeniAd !== undefined)            guncel.ad            = yeniAd?.trim();
    if (yeniSoyad !== undefined)         guncel.soyad         = yeniSoyad?.trim();
    if (yeniTelefon !== undefined)       guncel.telefon       = yeniTelefon?.trim();
    if (yeniDogumTarihi !== undefined)   guncel.dogumTarihi   = yeniDogumTarihi;
    if (yeniAvatar !== undefined)        guncel.avatar        = yeniAvatar;
    if (yeniCalendarColor !== undefined) guncel.calendarColor = yeniCalendarColor;

    const data = await apiRequest("/auth/profile", { method: "PATCH", body: JSON.stringify(guncel) });
    const serverProfile = data?.user?.profil || null;
    if (serverProfile) {
      saveSession({ user: data.user });
      setUser(normalizeUser(data.user));
      setProfil(serverProfile);
    } else {
      setProfil((p) => ({ ...p, ...guncel }));
    }

    // Herhangi bir alan değişince uyeler listesini de güncelle —
    // ad/soyad/telefon/dogumTarihi/avatar/renk hepsinde tüm sayfalar güncel kalsın
    if (onUyeGuncelle) {
      onUyeGuncelle({
        uid: user.uid,
        ...guncel,
        ...(serverProfile ? { avatarKind: serverProfile.avatarKind || null } : {}),
      });
    }
    return data;
  };

  const avatarYanitiUygula = (data, onUyeGuncelle) => {
    const avatar = data?.avatar || null;
    const avatarKind = data?.avatarKind || null;
    const hasStoredPhoto = Object.prototype.hasOwnProperty.call(data || {}, "profilePictureUrl");
    setProfil((current) => ({
      ...current,
      avatar,
      avatarKind,
      ...(hasStoredPhoto ? { profilePictureUrl: data.profilePictureUrl || null } : {}),
    }));
    setUser((current) => {
      if (!current) return current;
      const nextProfile = {
        ...(current.profil || {}),
        avatar,
        avatarKind,
        ...(hasStoredPhoto ? { profilePictureUrl: data.profilePictureUrl || null } : {}),
      };
      const next = {
        ...current,
        photoURL: avatar,
        photo_url: avatar,
        profil: nextProfile,
      };
      saveSession({ user: next });
      return next;
    });
    onUyeGuncelle?.({ uid: user?.uid, avatar });
    return data;
  };

  const profilFotografiYukle = async (file, onProgress, onUyeGuncelle) => {
    if (!user || !file) throw new Error("PROFILE_PHOTO_REQUIRED");
    const data = await profilFotografiYukleService(file, onProgress);
    return avatarYanitiUygula(data, onUyeGuncelle);
  };

  const profilFotografiSil = async (fallbackAvatar, onUyeGuncelle) => {
    if (!user) return null;
    const data = await profilFotografiSilService(fallbackAvatar || null);
    return avatarYanitiUygula(data, onUyeGuncelle);
  };

  /* ── Şifre güncelle ── */
  const sifreGuncelle = async ({ mevcutSifre, yeniSifre }) => {
    const data = await apiRequest("/auth/change-password", {
      method: "POST",
      skipAuthRefresh: true,
      body: JSON.stringify({ currentPassword: mevcutSifre, newPassword: yeniSifre }),
    });
    if (data?.user) {
      saveSession({ user: data.user });
      setUser(normalizeUser(data.user));
      setProfil(data.user?.profil || data.user);
    }
    return data;
  };

  /* ── Şifremi unuttum ── */
  const sifremiUnuttum = async () => {
    setHata(""); setBilgiMesaji("");
    if (!loginBilgi.trim()) { setHata(rt("auth.error.resetInfoRequired")); return; }
    try {
      setYukleniyor(true);
      await apiRequest("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ login: loginBilgi.trim(), lang: aktifDil || "tr" }),
      });
      setBilgiMesaji(rt("auth.info.resetSent"));
    } catch (e) {
      setHata(e.message);
    } finally {
      setYukleniyor(false);
    }
  };

  /* ── Dil güncelle ── */
  const dilGuncelle = async (newLang) => {
    if (!user?.uid || !LANGUAGES.includes(newLang)) return;
    try {
      await apiRequest("/auth/profile", {
        method: "PATCH",
        body: JSON.stringify({ language: newLang }),
      });
      // (Firestore yazması kaldırıldı — backend yeterli)
    } catch (e) {
      console.warn("Dil güncellenemedi:", e);
    }
  };


  /* ── Güvenli veri dışa aktarma ── */
  const verileriDisaAktar = async () => {
    const data = await apiRequest("/auth/export-my-data");
    const exportedAt = data?.exportedAt ? new Date(data.exportedAt) : new Date();
    const safeName = String(data?.profile?.username || data?.profile?.displayName || "kullanici")
      .toLowerCase()
      .replace(/[^a-z0-9ğüşıöç_-]+/gi, "-")
      .replace(/^-+|-+$/g, "") || "kullanici";
    const datePart = exportedAt.toISOString().slice(0, 10);
    const fileName = `aile-yonetimi-verilerim-${safeName}-${datePart}.json`;
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json;charset=utf-8" });
    const file = new File([blob], fileName, { type: "application/json" });

    // ANDROID: navigator.canShare({files}) Android WebView'de false döndüğü ve
    // <a download> Android'de indirmeyi hiç başlatmadığı için "Verilerimi indir"
    // butonu sessizce hiçbir şey yapmıyordu. Dosyayı önbelleğe yazıp Android'in
    // yerleşik paylaş/kaydet sayfasını açıyoruz — kullanıcı JSON'u "Dosyalara
    // kaydet" ile saklayabilir. Depolama izni gerektirmez.
    if (isAndroidPlatform()) {
      const paylasildi = await androidDosyaPaylas(blob, fileName, "Aile Yönetimi Verilerim");
      if (paylasildi) return { ok: true, shared: true };
    }

    // iPhone/Capacitor WebView için en güvenilir yol: paylaşım menüsü → Dosyalara Kaydet.
    if (navigator?.canShare?.({ files: [file] }) && navigator?.share) {
      try {
        await navigator.share({
          files: [file],
          title: "Aile Yönetimi Verilerim",
        });
        return { ok: true, shared: true };
      } catch (e) {
        // Kullanıcı paylaşımı iptal ettiyse klasik indirme fallback'i çalışsın.
      }
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      try { document.body.removeChild(a); } catch {}
      URL.revokeObjectURL(url);
    }, 1000);
    return { ok: true, shared: false };
  };

  /* ── Doğrulama mailini tekrar gönder ── */
  const dogrulamaMailiTekrarGonder = async () => {
    setHata(""); setBilgiMesaji("");
    try {
      setYukleniyor(true);
      const data = await apiRequest("/auth/send-verification", {
        method: "POST",
        body: JSON.stringify({ lang: aktifDil || "tr" }),
      });
      setBilgiMesaji(
        data?.alreadyVerified
          ? rt("auth.info.emailVerified")
          : (rt("auth.info.verificationResent") || rt("auth.info.verificationSent"))
      );
      setDogrulamaMailiGonderildi(true);
      return true;
    } catch (e) {
      setHata(e.message);
      setDogrulamaMailiGonderildi(false);
      return false;
    } finally {
      setYukleniyor(false);
    }
  };

  const dogrulamaEmailDegistir = async (nextEmail, password) => {
    setHata(""); setBilgiMesaji("");
    const normalizedNextEmail = String(nextEmail || "").trim().toLowerCase();
    const currentEmail = String(user?.email || email || "").trim().toLowerCase();

    // Mevcut adresi yeniden PATCH etmek hem gereksiz bir API çağrısıdır hem de
    // eski sunucu sürümlerinde anlamsız bir 404 üretir. Aynı adrese tekrar mail
    // gönderme işlemi, kendi bekleme süresi olan ayrı butondan yapılır.
    if (isSameVerificationEmail(normalizedNextEmail, currentEmail)) {
      setHata(rt("auth.error.verificationEmailUnchanged"));
      return { ok: false, sent: false, code: "EMAIL_UNCHANGED" };
    }

    try {
      setYukleniyor(true);
      const data = await apiRequest("/auth/unverified-email", {
        method: "PATCH",
        body: JSON.stringify({
          email: normalizedNextEmail,
          password: String(password || ""),
          lang: aktifDil || "tr",
        }),
      });
      saveSession({ user: data.user });
      const normalizedUser = normalizeUser(data.user);
      setUser(normalizedUser);
      setProfil((current) => mergeAuthProfilePreservingFamilyContext(current, data.user?.profil || data.user));
      setEmail(String(data.user?.email || nextEmail || "").trim().toLowerCase());
      const sent = data?.verificationEmail?.sent === true;
      setDogrulamaMailiGonderildi(sent);
      if (sent) setBilgiMesaji(rt("auth.info.verificationAddressUpdated"));
      else setHata(rt("auth.error.verificationInitialSendFailed"));
      return { ok: true, sent, email: data.user?.email || nextEmail };
    } catch (e) {
      const code = String(e?.code || "").toUpperCase();
      if (code === "INVALID_PASSWORD") setHata(rt("settings.privacy.invalidPassword"));
      else if (code === "EMAIL_TAKEN") setHata(rt("auth.error.emailTaken"));
      else if (code === "INVALID_EMAIL") setHata(rt("auth.error.emailInvalid"));
      else if (e?.status === 404) setHata(rt("auth.error.verificationEmailChangeUnavailable"));
      else if (!e?.message || /^\u0130stek ba\u015far\u0131s\u0131z \(\d+\)$/i.test(e.message)) {
        setHata(rt("auth.error.verificationEmailUpdateFailed"));
      } else setHata(e.message);
      return { ok: false, sent: false };
    } finally {
      setYukleniyor(false);
    }
  };

  /* ── E-posta doğrulandı mı kontrol et ──
     Kullanıcı mail içindeki linke tıkladıktan sonra (ayrı bir Safari sekmesinde
     açılır, bu oturuma dahil değildir) bu uygulama oturumuna dönüp "Kontrol Et"
     butonuna basar; backend'den güncel durumu çeker. */
  const emailDogrulamaKontrolEt = async () => {
    setHata(""); setBilgiMesaji("");
    try {
      setYukleniyor(true);
      const data = await apiRequest("/auth/me");
      const dogrulandi = !!data?.user?.emailVerified;
      setEmailDogrulandi(dogrulandi);
      if (dogrulandi) {
        const normalizedUser = normalizeUser(data.user);
        saveSession({ user: data.user });
        setUser(normalizedUser);
      } else {
        setHata(rt("auth.error.emailNotVerified"));
      }
      return dogrulandi;
    } catch (e) {
      setHata(e.message);
      return false;
    } finally {
      setYukleniyor(false);
    }
  };
  const hesapSil = async (password, provider, deletionSurvey = null) => {
    const cleanPassword = String(password || "");
    const hasPassword = user?.hasPassword !== false;
    let body;

    const socialProvider = provider || (!hasPassword
      ? user?.authProviders?.find((item) => item === "apple.com" || item === "google.com")
      : "");
    if (!hasPassword && !socialProvider) {
      const error = new Error(rt("auth.social.error.noLinkedProvider"));
      error.code = "SOCIAL_PROVIDER_REQUIRED";
      throw error;
    }
    if (socialProvider) {
      const social = await startSocialAuthentication(socialProvider, aktifDil || "tr");
      const ticketData = await apiRequest("/auth/account/deletion-ticket", {
        method: "POST",
        body: JSON.stringify({ idToken: social.idToken }),
      });
      if (socialProvider === "apple.com") {
        await revokeAppleCredential(social.credential);
      }
      body = { deletionTicket: ticketData.deletionTicket };
    } else {
      if (!cleanPassword) throw new Error(rt("settings.privacy.passwordRequired") || "Devam etmek için şifrenizi girin.");
      body = { password: cleanPassword };
    }

    if (deletionSurvey) {
      const appContext = await getAppContext();
      body = buildAccountDeletionRequestBody({ body, deletionSurvey, appContext, language: aktifDil });
    }

    await apiRequest("/auth/account", {
      method: "DELETE",
      body: JSON.stringify(body),
    });

    profilDinlemeyiDurdur();
    await clearNativeBackgroundLocationSession().catch(() => {});
    clearSession();
    await signOutSocialAuthentication();
    setUser(null);
    setProfil(null);
    setEmailDogrulandi(false);
    return { ok: true };
  };

  return {
    user, setUser, profil, setProfil, authKontrolEdiliyor, emailDogrulandi,
    dogrulamaMailiGonderildi,
    loginBilgi, setLoginBilgi, sifreGoster, setSifreGoster,
    email, setEmail, sifre, setSifre, kayitSifre, setKayitSifre, sifreInputRef,
    hata, setHata, bilgiMesaji, setBilgiMesaji, yukleniyor, kayitModu, setKayitModu,
    bekleyenSosyalKayit, kayitAlanHatasi, setKayitAlanHatasi,
    yasalOnay, setYasalOnay,
    ad, setAd, soyad, setSoyad,
    dogumTarihi, setDogumTarihi, cinsiyet, setCinsiyet,
    telefon, setTelefon, kullaniciAdi, setKullaniciAdi,
    rol, setRol, kanGrubu, setKanGrubu, boy, setBoy, kilo, setKilo,
    startAuthWatch, kayitOl, girisYap, sosyalGirisYap, sosyalAkisiIptalEt,
    sosyalHesapBagla, cikisYap, hesapSil,
    dogrulamaMailiTekrarGonder, dogrulamaEmailDegistir, emailDogrulamaKontrolEt, sifremiUnuttum,
    profilGuncelle, profilFotografiYukle, profilFotografiSil,
    sifreGuncelle, dilGuncelle, verileriDisaAktar,
  };
}
